import { Block, LinkOff, Work, WorkOutlined } from "@mui/icons-material";
import TableRowsOutlined from "@mui/icons-material/TableRowsOutlined";
import { Box, Chip } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../../../components/InHouseTooltip/InHouseTooltip";
import { JumpingDots } from "../../../../../../components/UIToolkit/Animations/JumpingDots";
import { EmptyState } from "../../../../../../components/UIToolkit/molecules/EmptyState";
import { SystemDataGrid } from "../../../../../../components/UIToolkit/Tables/SystemDataGrid";
import { NEUTRAL } from "../../../../../../theme/tokens";
import { type CertificationJobMapping, Position } from "../../../../../../utils/models";
import { PaneHeader } from "./PaneHeader";

interface HrisJobOverviewProps {
	mappings: CertificationJobMapping[];
	/** Set of roleStrings whose save is debounced/in flight — drives the per-row loading dots. */
	pendingKeys: Set<string>;
}

/**
 * Right pane of the HRIS Job Alignment view. Read-only summary grid with three columns:
 *   HRIS JOB | IN-HOUSE MAPPING | ROLE
 *
 * Source of truth = the same `CertificationJobMapping[]` the Raw Table grid uses (already
 * filtered by the Pretty Mapping parent). This pane is strictly read-only — all editing happens
 * in the left pane.
 *
 * The ROLE column shows the mapping's raw role code (e.g. RN/CNA) untranslated, matching the
 * Raw Table grid; rows without a role (DontSync, defaults-only) render a blank cell.
 * A PPD STATUS column is intentionally omitted for now. TODO @eitan add the include ppd toggle once we have uria's feature
 *
 */
export const HrisJobOverview: FC<HrisJobOverviewProps> = ({ mappings, pendingKeys }) => {
	const { t } = useTranslation();

	const columns: GridColDef<CertificationJobMapping>[] = useMemo(
		() => [
			{
				field: "roleString",
				headerName: t("hris.mapping.column.hrisJob"),
				flex: 1,
				minWidth: 200,
			},
			{
				field: "job",
				headerName: t("hris.mapping.column.jobName"),
				flex: 1,
				minWidth: 200,
				sortable: false,
				display: "flex",
				renderCell: (params) => {
					const row = params.row;
					if (pendingKeys.has(row.roleString)) {
						return <JumpingDots data-testid={`hris-mapping-overview-pending-${row.roleString}`} />;
					}
					if (row.position === Position.DontSync) {
						return (
							<Chip
								size="small"
								icon={<Block />}
								label={t("hris.mapping.dontSync")}
								variant="outlined"
								color="error"
								data-testid={`hris-mapping-overview-dont-sync-${row.roleString}`}
							/>
						);
					}
					const syncJob = row.jobs[0];
					if (!syncJob) {
						return (
							<InHouseTooltip title={t("hris.mapping.notMapped.tooltip")}>
								<Chip
									size="small"
									icon={<LinkOff />}
									label={t("hris.mapping.notMapped")}
									variant="outlined"
									color="warning"
									data-testid={`hris-mapping-overview-not-mapped-${row.roleString}`}
								/>
							</InHouseTooltip>
						);
					}
					// Synced: the job-name chip IS the resolved mapping; tooltip kept for parity with the other states.
					// Additional jobs (DAT-466) render as muted chips after it.
					return (
						<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
							<InHouseTooltip title={t("hris.mapping.synced.tooltip", { job: syncJob.displayName ?? syncJob.name })}>
								<Chip
									size="small"
									icon={<Work />}
									label={syncJob.displayName ?? syncJob.name}
									variant="outlined"
									color="primary"
									data-testid={`hris-mapping-overview-synced-${row.roleString}`}
								/>
							</InHouseTooltip>
							{row.jobs.slice(1).map((job) => (
								<InHouseTooltip key={job.id} title={t("hris.mapping.additionalJobs.tooltip", { job: job.displayName ?? job.name })}>
									<Chip
										size="small"
										icon={<WorkOutlined />}
										label={job.displayName ?? job.name}
										variant="outlined"
										data-testid={`hris-mapping-overview-additional-${row.roleString}-${job.name}`}
									/>
								</InHouseTooltip>
							))}
						</Box>
					);
				},
			},
			{
				field: "role",
				headerName: t("hris.mapping.column.role"),
				minWidth: 120,
			},
			// TODO @eitan re-enable the PPD Status column when Uria's include-PPD toggle ships.
		],
		[t, pendingKeys],
	);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1,
				height: "100%",
				overflow: "hidden",
				// Read-only "result" surface so the right pane reads as a summary, not a second editor.
				backgroundColor: NEUTRAL[50],
				border: `1px solid ${NEUTRAL[200]}`,
				borderRadius: 1.5,
				p: 1.5,
			}}
			data-testid="hris-mapping-job-overview-pane"
		>
			<PaneHeader
				title={t("hris.mapping.alignment.resultPane.title")}
				hint={t("hris.mapping.alignment.resultPane.hint")}
				dataTestId="hris-mapping-result-pane-header"
			/>
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<SystemDataGrid<CertificationJobMapping>
					rows={mappings}
					columns={columns}
					getRowId={(row) => row.roleString}
					emptyState={<EmptyState icon={<TableRowsOutlined />} title={t("hris.mapping.overview.empty.title")} hint={t("hris.mapping.overview.empty.hint")} />}
					dataTestId="hris-mapping-job-overview"
				/>
			</Box>
		</Box>
	);
};
